import { FunctionComponent, useCallback, useMemo, useState } from "react";
import type { IFormikProps, IProps } from "./date-picker-input.types";
import { Input } from "../input";
import { Popover } from "../popover";
import { faCalendar } from "@fortawesome/pro-solid-svg-icons";
import { Icon } from "../icon";
import { DropdownIcon } from "../dropdown-icon";
import { format } from "date-fns";
import { getRelativeDatesInfo } from "./date-picker-input.utilities";
import { useField, useFormikContext } from "formik";
import { DatePickerPanel, DatePickerPanelValue } from "./date-picker-panel";
import { DATE_PICKER_RADIUS } from "./date-picker-input.constants";

/**
 * DatePickerInput component allows users to select a date range with optional time selection.
 * It supports relative date presets and custom date ranges. The popover's body is `DatePickerPanel`, which is mounted
 * afresh on every open, so it always opens on the current field values.
 *
 * @param {IProps} props - The properties for the DatePickerInput component.
 * @param {Date} props.startDate - The initial start date of the range.
 * @param {Date} props.endDate - The initial end date of the range.
 * @param {number} [props.limitSelectionMonths] - The maximum number of months allowed for selection.
 * @param {number} [props.limitPastDays] - The number of past days to limit the selection.
 * @param {RelativeDates} [props.relativeDateTime] - The relative date preset to use.
 * @param {boolean} [props.showRelativeDateValues] - Whether to show relative date presets.
 * @param {boolean} [props.allowTimeSelect]
 * @param {Date} [props.minDate] - The minimum date allowed for selection.
 * @param {Date} [props.maxDate] - The maximum date allowed for selection.
 * @param {RelativeDates[]} [props.omittedRelativeDates] - The relative dates to omit from the dropdown.
 * @returns {JSX.Element} The rendered DatePickerInput component.
 */
const DatePickerInput = ({
    startDate,
    endDate,
    limitSelectionMonths,
    limitPastDays,
    relativeDateTime,
    showRelativeDateValues,
    allowTimeSelect,
    startDateName = "startDateTime",
    endDateName = "endDateTime",
    relativeDateName = "relativeDateTime",
    variant,
    disabled,
    activable,
    onChange,
    displayIcon = true,
    minDate,
    maxDate = new Date(),
    infoLabel,
    transformRelativeDateTime = false,
    omittedRelativeDates,
}: IProps) => {
    const [opened, setOpened] = useState(false);
    const { setFieldValue } = useFormikContext();

    const displayText = useMemo(() => {
        if (relativeDateTime) {
            return getRelativeDatesInfo()[relativeDateTime].formatedName;
        }

        const formattedStartDate = startDate ? format(new Date(startDate), "dd/MM/yy") : "";
        const formattedEndDate = endDate ? format(new Date(endDate), "dd/MM/yy") : "";
        return `${formattedStartDate} - ${formattedEndDate}`;
    }, [startDate, endDate, relativeDateTime]);

    /**
     * Write what the panel applied to the form: the preset (and, with `transformRelativeDateTime`, its resolved range),
     * or the absolute range, clearing the other.
     * @param applied The preset or range the panel's Done applied.
     */
    const handleApply = useCallback(
        (applied: DatePickerPanelValue) => {
            setOpened(false);
            if ("relativeDateTime" in applied) {
                const preset = applied.relativeDateTime;
                setFieldValue(relativeDateName, preset);
                setFieldValue(
                    startDateName,
                    transformRelativeDateTime ? getRelativeDatesInfo()[preset].startDate : undefined
                );
                setFieldValue(
                    endDateName,
                    transformRelativeDateTime ? getRelativeDatesInfo()[preset].endDate : undefined
                );
                onChange?.(null, null, preset);
            } else {
                setFieldValue(startDateName, applied.startDateTime);
                setFieldValue(endDateName, applied.endDateTime);
                setFieldValue(relativeDateName, undefined);
                onChange?.(applied.startDateTime, applied.endDateTime, null);
            }
        },
        [endDateName, onChange, relativeDateName, setFieldValue, startDateName, transformRelativeDateTime]
    );

    /** Close without writing anything; the panel is unmounted, so the next open starts on the field's values again. */
    const handleCancel = useCallback(() => setOpened(false), []);

    return (
        <Popover
            position="bottom-start"
            shadow="md"
            disabled={disabled}
            opened={opened}
            onDismiss={() => setOpened(false)}
        >
            <Popover.Target>
                <Input
                    // The input used to read "has a start or end time", which the popover's state always had from its
                    // first effect on, so an activable input was always active. The state now lives in the panel.
                    active={activable}
                    leftSection={displayIcon ? <Icon icon={faCalendar} size="sm" /> : undefined}
                    rightSection={<DropdownIcon isOpen={opened} />}
                    component="button"
                    pointer
                    type="button"
                    onClick={() => setOpened(!opened)}
                    data-testid="date-range-filter"
                    variant={variant}
                    disabled={disabled}
                >
                    {displayText}
                </Input>
            </Popover.Target>
            <Popover.Dropdown bdrs={DATE_PICKER_RADIUS} p="0" data-testid="date-picker-container">
                <DatePickerPanel
                    startDate={startDate}
                    endDate={endDate}
                    relativeDateTime={relativeDateTime}
                    limitSelectionMonths={limitSelectionMonths}
                    limitPastDays={limitPastDays}
                    showRelativeDateValues={showRelativeDateValues}
                    allowTimeSelect={allowTimeSelect}
                    minDate={minDate}
                    maxDate={maxDate}
                    infoLabel={infoLabel}
                    omittedRelativeDates={omittedRelativeDates}
                    onApply={handleApply}
                    onCancel={handleCancel}
                />
            </Popover.Dropdown>
        </Popover>
    );
};

const DatePickerInputFormik: FunctionComponent<IFormikProps> = ({ validate, ...otherProps }) => {
    const [startDateField] = useField(otherProps.startDateName);
    const [endDateField] = useField(otherProps.endDateName);
    const [relativeDateField] = useField(otherProps.relativeDateName ?? "relativeDateTime");
    return (
        <Input.Formik.Wrapper name={otherProps.startDateName} validate={validate}>
            <DatePickerInput
                startDate={startDateField.value}
                endDate={endDateField.value}
                relativeDateTime={relativeDateField.value}
                {...otherProps}
            />
        </Input.Formik.Wrapper>
    );
};

DatePickerInput.Formik = DatePickerInputFormik;

export { DatePickerInput };

export default DatePickerInput;
